/**
 * Pure Mapbox Geocoding helpers.
 *
 * Extracted from the voter dialog so the voter flow and the finance mailing-
 * address flow share one source of truth for parsing/fetching. Keep the fetch
 * shape and parsing byte-identical to what the voter dialog historically sent,
 * so nothing about the on-the-wire request changes.
 */

// Mapbox Geocoding API types
export interface MapboxContext {
  id: string;
  text: string;
  short_code?: string;
}

export interface MapboxFeature {
  id: string;
  place_name: string;
  center: [number, number]; // [longitude, latitude]
  context?: MapboxContext[];
  address?: string; // Street number
  text: string; // Street name
}

interface MapboxGeocodingResponse {
  features: MapboxFeature[];
}

// Parsed address result
export interface ParsedAddress {
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  latitude: number;
  longitude: number;
}

/**
 * Parse a Mapbox geocoding feature into address components.
 */
export function parseMapboxAddress(feature: MapboxFeature): ParsedAddress {
  const streetNumber = feature.address || '';
  const streetName = feature.text || '';
  const streetAddress = streetNumber ? `${streetNumber} ${streetName}` : streetName;

  // Extract components from the context array.
  let city = '';
  let state = '';
  let zip = '';

  for (const ctx of feature.context || []) {
    if (ctx.id.startsWith('place.')) {
      city = ctx.text;
    } else if (ctx.id.startsWith('region.')) {
      // Prefer short_code if present (e.g., "US-AL" -> "AL"), else the full name.
      state = ctx.short_code?.replace('US-', '') || ctx.text;
    } else if (ctx.id.startsWith('postcode.')) {
      zip = ctx.text;
    }
  }

  return {
    streetAddress,
    city,
    state,
    zip,
    latitude: feature.center[1],
    longitude: feature.center[0],
  };
}

export interface SearchMapboxOptions {
  /** Location bias — nudges results toward the given point. */
  proximity?: { lng: number; lat: number };
  /** Abort signal so callers can cancel a stale in-flight request. */
  signal?: AbortSignal;
}

/**
 * Search US addresses via the Mapbox Geocoding API.
 *
 * Returns `[]` (without hitting the network) when there is no token or the
 * query is shorter than 3 characters. Any non-ok response or thrown error is
 * logged and swallowed as `[]`, so callers never have to handle the failure
 * path — autocomplete just goes quiet.
 */
export async function searchMapboxAddresses(
  query: string,
  token: string,
  opts: SearchMapboxOptions = {},
): Promise<MapboxFeature[]> {
  if (!token || query.length < 3) {
    return [];
  }

  const params = new URLSearchParams({
    access_token: token,
    autocomplete: 'true',
    country: 'US',
    types: 'address',
    limit: '5',
  });

  // Add location bias if the caller provided the user's location.
  if (opts.proximity) {
    params.set('proximity', `${opts.proximity.lng},${opts.proximity.lat}`);
  }

  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?${params}`,
      opts.signal ? { signal: opts.signal } : undefined,
    );

    if (!response.ok) {
      console.error('Mapbox geocoding error:', response.status);
      return [];
    }

    const data: MapboxGeocodingResponse = await response.json();
    return data.features || [];
  } catch (err) {
    console.error('Error fetching address suggestions:', err);
    return [];
  }
}
